// Client layer (TS-2.5.03). The read-only "larger view" a detail card's Expand button opens
// (decisions.md D-280): the same project name/status/percent/milestones the card already shows,
// plus the product + owners join the card's own header line summarizes, and an "Open project"
// link into the editable form. Built on the shared `Modal` (src/components/Modal.tsx) rather than
// a bespoke overlay, and reuses `MilestoneTimeline` and the same status-pill styling
// `ProjectDetailCard.tsx` already owns instead of re-deriving either.
//
// "Notes" (this batch's owner-facing name for the larger view) is `ProjectView.description` -
// the roadmap's composed view carries no separate stored-notes list of its own (that lives on
// `ProjectDetail.notes`, the project EDIT screen's own DTO, D-266/D-270's scoping keeps the
// roadmap composition to what ReportModelEngine actually derives); the description is the one
// free-text narrative field this view has, and the card already renders it under that role.
//
// D-285/D-286 addition: this project's own OPEN risk/blocker notes (`ProjectView.risks` filtered
// on `RiskItem.isOpen`), a genuine improvement over D-280's original scope - that batch mapped
// "Notes" to `description` alone because the roadmap composition carried nothing else free-text;
// `RiskItem.isOpen` (D-285) is new since then, and a reader with an open blocker on this project
// had no way to see it from this modal until now. Same compact type-badge-plus-text pattern the
// enriched `StatCardMembersModal` uses (D-284's own list, one project row).
import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { Modal } from "../../components/Modal";
import { resolveColor } from "./lib/colorTokens";
import { readableTextColor } from "./lib/contrast";
import { MilestoneTimeline } from "./MilestoneTimeline";
import { RiskTypeBadge } from "./RiskTypeBadge";
import type { ProjectOwnerInfo } from "./lib/resolveProjectOwners";
import type { ProjectView } from "../../api/generated/contracts";
import { strings } from "../../strings";

const S = strings.roadmap.detailModal;
const C = strings.roadmap.detailCard;

export interface ProjectDetailModalProps {
  readonly open: boolean;
  readonly project: ProjectView;
  readonly ownerInfo: ProjectOwnerInfo;
  readonly onClose: () => void;
}

export function ProjectDetailModal({ open, project, ownerInfo, onClose }: ProjectDetailModalProps) {
  const openRisks = project.risks.filter((risk) => risk.isOpen);

  return (
    <Modal open={open} title={project.name} onClose={onClose} closeLabel={S.close}>
      <span
        className="eg-detail-card__pill"
        style={
          {
            "--eg-chip-color": resolveColor(project.status.colorHex),
            "--eg-chip-ink": readableTextColor(project.status.colorHex),
          } as CSSProperties
        }
      >
        {project.status.canonical}
      </span>

      <dl className="eg-modal__facts">
        {ownerInfo.productName && (
          <>
            <dt>{S.product}</dt>
            <dd>{ownerInfo.productName}</dd>
          </>
        )}
        <dt>{C.lead}</dt>
        <dd>{project.owner || C.noOwner}</dd>
        <dt>{C.businessOwner}</dt>
        <dd>{ownerInfo.businessOwner || C.noOwner}</dd>
        <dt>% complete</dt>
        <dd>{project.percentComplete === null ? "—" : `${Math.round(project.percentComplete)}%`}</dd>
        <dt>{S.goLive}</dt>
        <dd>{project.goLive.label}</dd>
      </dl>

      {project.milestones.length > 0 && <MilestoneTimeline milestones={project.milestones} />}

      {project.description && (
        <div>
          <h3>{S.notes}</h3>
          <p>{project.description}</p>
        </div>
      )}

      {openRisks.length > 0 && (
        <div>
          <h3>{S.openRisks}</h3>
          <ul className="eg-stat-card-modal__project-risks">
            {openRisks.map((risk) => (
              <li key={risk.key} className="eg-stat-card-modal__project-risk">
                <RiskTypeBadge note={risk} />
                <span>{risk.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Link to={`/projects/${project.key}`}>{S.openProject}</Link>
    </Modal>
  );
}
